import styled, { css } from 'styled-components';
import { em, rem } from 'styles';
import { PALETTE, STONE_CLASSES, MUST_PICK_COLOR } from '../../fields.constants';

const classColor = key => STONE_CLASSES.find(item => item.key === key).color;

const THUMB_PX = 18;
const SLIDER_PX = 22;

const thumb = css`
  width: ${THUMB_PX}px;
  height: ${THUMB_PX}px;
  border-radius: 50%;
  background: ${PALETTE.brand};
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

export const Card = styled.section`
  padding: ${em(12)} ${em(11)};
  background: #fff;
  border: ${em(1)} solid ${PALETTE.line};
  border-radius: ${em(6)};
`;

export const Title = styled.h3`
  margin: 0 0 ${em(10)};
  font-size: ${rem(15)};
  color: ${PALETTE.ink};
`;

export const Group = styled.div`
  margin-bottom: ${em(14)};
`;

export const Label = styled.label`
  display: flex;
  justify-content: space-between;
  gap: ${em(8)};
  margin-bottom: ${em(6)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.muted};
`;

export const ThresholdLabel = styled.label`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${em(6)};
  font-size: ${rem(13)};
  color: ${PALETTE.ink};
`;

export const CmInput = styled.input`
  width: ${em(62)};
  padding: ${em(3)} ${em(6)};
  font-size: ${rem(13)};
  font-weight: 500;
  color: ${PALETTE.ink};
  text-align: center;
  background: #fff;
  border: ${em(1)} solid ${PALETTE.line};
  border-radius: ${em(4)};

  &[aria-invalid='true'] {
    border-color: ${MUST_PICK_COLOR};
  }
`;

export const LockNote = styled.p`
  margin: ${em(6)} 0 0;
  font-size: ${rem(12)};
  line-height: 1.4;
  color: ${PALETTE.muted};
`;

export const Hint = styled.p`
  margin: ${em(4)} 0 0;
  font-size: ${rem(12)};
  color: ${MUST_PICK_COLOR};
`;

export const Gauges = styled.div`
  display: grid;
  gap: ${em(6)};
`;

export const Gauge = styled.div`
  display: grid;
  grid-template-columns: ${em(100)} 1fr ${em(76)};
  align-items: center;
  gap: ${em(8)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.muted};
`;

export const GaugeLabel = styled.span`
  white-space: nowrap;
  color: ${PALETTE.ink};
`;

export const GaugeTrack = styled.span`
  height: ${em(8)};
  overflow: hidden;
  background: ${PALETTE.line};
  border-radius: ${em(999)};
`;

export const GaugeFill = styled.span`
  display: block;
  height: 100%;
  width: ${({ $fraction }) => `${($fraction * 100).toFixed(1)}%`};
  background: ${({ $color }) => $color};
`;

export const GaugeNote = styled.span`
  white-space: nowrap;
  text-align: right;
`;

export const Warning = styled.p`
  margin: ${em(8)} 0 0;
  padding: ${em(7)} ${em(9)};
  font-size: ${rem(12.5)};
  line-height: 1.4;
  color: ${PALETTE.lateText};
  background: ${PALETTE.lateBg};
  border: ${em(1)} solid ${PALETTE.lateBorder};
  border-radius: ${em(4)};
`;

export const RateValue = styled.b`
  white-space: nowrap;
  font-weight: 500;
  color: ${PALETTE.ink};
  font-variant-numeric: tabular-nums;
`;

export const SliderWrap = styled.div`
  position: relative;
  padding-bottom: ${em(15)};
`;

export const Range = styled.input`
  display: block;
  width: 100%;
  height: ${SLIDER_PX}px;
  margin: 0;
  background: transparent;
  accent-color: ${PALETTE.brand};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-slider-runnable-track {
    height: 5px;
    border-radius: 3px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    margin-top: -6.5px;
    ${thumb}
  }

  &::-moz-range-thumb {
    ${thumb}
  }
`;

export const SweetSpot = styled.div`
  position: absolute;
  top: 0;
  left: calc(
    ${THUMB_PX / 2}px + ${({ $fraction }) => ($fraction * 100).toFixed(2)}% -
      ${({ $fraction }) => ($fraction * THUMB_PX).toFixed(1)}px
  );
  transform: translateX(-50%);
  pointer-events: none;
  text-align: center;
  font-size: ${rem(10.5)};
  color: ${PALETTE.amber};
  font-variant-numeric: tabular-nums;

  span {
    display: block;
    width: 2px;
    height: ${SLIDER_PX}px;
    margin: 0 auto;
    background: ${PALETTE.amber};
    opacity: 0.75;
  }
`;

export const RateEnds = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${em(-4)};
  font-size: ${rem(11)};
  color: ${PALETTE.faint};
  font-variant-numeric: tabular-nums;
`;

export const RateEffect = styled.div`
  margin-top: ${em(9)};
  font-size: ${rem(12.5)};
  line-height: 1.45;
  color: ${PALETTE.muted};
`;

export const Tone = styled.span`
  font-weight: 500;
  color: ${({ $tone }) =>
    ({
      strong: PALETTE.ink,
      band: classColor('l'),
      warn: classColor('xl')
    }[$tone])};
`;

export const Result = styled.div`
  padding: ${em(12)};
  background: ${PALETTE.panel};
  border: ${em(1)} solid ${PALETTE.line};
  border-radius: ${em(6)};
  opacity: ${({ $stale }) => ($stale ? 0.6 : 1)};
  transition: opacity 0.15s;
`;

export const Count = styled.div`
  font-size: ${rem(29)};
  font-weight: 500;
  line-height: 1;
  color: ${PALETTE.ink};
  font-variant-numeric: tabular-nums;
`;

export const Caption = styled.div`
  margin-top: ${em(4)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.muted};
`;

export const Split = styled.div`
  display: flex;
  gap: ${em(14)};
  margin-top: ${em(8)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.ink};
  font-variant-numeric: tabular-nums;
`;

export const SplitItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${em(6)};

  i {
    display: block;
    width: ${em(10)};
    height: ${em(10)};
    background: ${({ $color }) => $color};
    border: ${em(1)} solid rgba(0, 0, 0, 0.2);
  }
`;

export const Sentence = styled.p`
  margin: ${em(9)} 0 0;
  padding-top: ${em(9)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.muted};
  border-top: ${em(1)} solid ${PALETTE.line};
`;

export const Message = styled.div`
  display: flex;
  align-items: center;
  gap: ${em(10)};
  font-size: ${rem(12.5)};
  color: ${PALETTE.muted};
`;
